import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase URL or Anon Key. Please check your .env file')
}

// Configuration du client Supabase avec des en-têtes personnalisés
const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'apikey': supabaseAnonKey
    }
  },
  db: {
    schema: 'public'
  }
})

const toIsoString = (value) => {
  if (value === null || value === undefined) return null
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'number') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }
  if (typeof value === 'string') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }
  return null
}

const timestampToMillis = (value) => {
  if (!value) return null
  const date = new Date(value)
  const time = date.getTime()
  return Number.isNaN(time) ? null : time
}

const normalizeNumber = (value, fallback = 0) => {
  const num = Number(value)
  return Number.isNaN(num) ? fallback : num
}

const mapPlantRowToState = (row) => ({
  id: row.plant_id,
  type: row.plant_type,
  config: row.config || null,
  position: row.position || null,
  progress: row.progress ?? 0,
  stage: row.stage ?? 0,
  branches: row.branches || [],
  plantedAt: timestampToMillis(row.planted_at),
  lastSeedGeneration: timestampToMillis(row.last_seed_generation),
  pendingSeed: Boolean(row.pending_seed),
  seedProgress: normalizeNumber(row.seed_progress),
  seedClickBonus: normalizeNumber(row.seed_click_bonus),
})

const serializePlantForDb = (userId, plant, index = 0) => ({
  user_id: userId,
  plant_id: plant.id || `plant_${index}`,
  plant_type: plant.type || 'default',
  config: plant.config || null,
  position: plant.position || null,
  progress: normalizeNumber(plant.progress),
  stage: normalizeNumber(plant.stage),
  branches: plant.branches || [],
  planted_at: toIsoString(plant.plantedAt),
  last_seed_generation: toIsoString(plant.lastSeedGeneration),
  pending_seed: Boolean(plant.pendingSeed),
  seed_progress: normalizeNumber(plant.seedProgress),
  seed_click_bonus: normalizeNumber(plant.seedClickBonus),
})

// Export du client configuré
export { supabase }

// Database helper functions
export const db = {
  // Save player data
  async savePlayerData(userId, data) {
    const nowIso = new Date().toISOString()
    const settingsPayload = {
      user_id: userId,
      inventory: data.inventory ?? [],
      selected_plant_id: data.selectedPlantId ?? null,
      current_plant: data.currentPlant ?? null,
      plant_progress: data.plantProgress ?? 0,
      plant_stage: data.plantStage ?? 0,
      pending_seed: Boolean(data.pendingSeed),
      seed_progress: data.seedProgress ?? 0,
      seed_click_bonus: data.seedClickBonus ?? 0,
      water_level: data.waterLevel ?? 100,
      last_water_refill: toIsoString(data.lastWaterRefill),
      water_refill_interval: data.waterRefillInterval ?? (13 * 60 * 60 * 1000),
      water_cooldown_until: toIsoString(data.waterCooldownUntil),
      auto_gardener_owned: Boolean(data.autoGardenerOwned),
      auto_gardener_last_tick: toIsoString(data.autoGardenerLastTick),
      auto_gardener_interval: data.autoGardenerInterval ?? 10000,
      auto_gardener_config: data.autoGardenerConfig ?? { clicksPerTick: 1, intervalMs: 10000 },
      selected_background: data.selectedBackground || 'background_garden',
      last_free_seed_claim: toIsoString(data.lastFreeSeedClaim),
      fertilizer_multiplier: data.fertilizerMultiplier ?? 1,
      fertilizer_active_until: toIsoString(data.fertilizerActiveUntil),
      watering_can_multiplier: data.wateringCanMultiplier ?? 1,
      herbarium: data.herbarium ? JSON.parse(JSON.stringify(data.herbarium)) : {},
      updated_at: nowIso,
    }

    const { error: settingsError } = await supabase
      .from('player_settings')
      .upsert(settingsPayload, { onConflict: 'user_id' })

    if (settingsError) throw settingsError

    const seedEntries = Object.entries(data.seedInventory || {})
      .map(([seedType, quantity]) => ({
        user_id: userId,
        seed_type: seedType,
        quantity: normalizeNumber(quantity),
      }))
      .filter((entry) => entry.quantity > 0)

    const seedTypes = seedEntries.map((entry) => entry.seed_type)

    if (seedTypes.length) {
      const seedTypeList = `(${seedTypes.map((type) => `"${type}"`).join(',')})`
      const { error: deleteMissingSeedTypesError } = await supabase
        .from('player_seeds')
        .delete()
        .eq('user_id', userId)
        .not('seed_type', 'in', seedTypeList)

      if (deleteMissingSeedTypesError) throw deleteMissingSeedTypesError
    } else {
      const { error: deleteAllSeedsError } = await supabase
        .from('player_seeds')
        .delete()
        .eq('user_id', userId)

      if (deleteAllSeedsError) throw deleteAllSeedsError
    }

    if (seedEntries.length) {
      const { error: upsertSeedsError } = await supabase
        .from('player_seeds')
        .upsert(seedEntries, { onConflict: 'user_id,seed_type' })
      if (upsertSeedsError) throw upsertSeedsError
    }

    const plantArray = Array.isArray(data.plants) ? data.plants : []
    const plantEntries = plantArray.map((plant, index) => serializePlantForDb(userId, plant, index))

    const { error: deletePlantsError } = await supabase
      .from('player_plants')
      .delete()
      .eq('user_id', userId)

    if (deletePlantsError) throw deletePlantsError

    if (plantEntries.length) {
      const { error: insertPlantsError } = await supabase
        .from('player_plants')
        .insert(plantEntries)
      if (insertPlantsError) throw insertPlantsError
    }

    const ownedTools = Array.isArray(data.ownedTools) ? data.ownedTools : []
    const ownedBackgrounds = Array.isArray(data.ownedBackgrounds) ? data.ownedBackgrounds : []

    const uniqueValues = (value, index, array) => array.indexOf(value) === index

    const toolEntries = [
      ...ownedTools
        .filter(Boolean)
        .filter(uniqueValues)
        .map((toolId) => ({
          user_id: userId,
          category: 'tool',
          tool_id: toolId,
          data: null,
        })),
      ...ownedBackgrounds
        .filter(Boolean)
        .filter(uniqueValues)
        .map((backgroundId) => ({
          user_id: userId,
          category: 'background',
          tool_id: backgroundId,
          data: { selected: backgroundId === data.selectedBackground },
        })),
    ]

    const { error: deleteToolsError } = await supabase
      .from('player_tools')
      .delete()
      .eq('user_id', userId)

    if (deleteToolsError) throw deleteToolsError

    if (toolEntries.length) {
      const { error: insertToolsError } = await supabase
        .from('player_tools')
        .insert(toolEntries)
      if (insertToolsError) throw insertToolsError
    }
  },
  
  // Load player data
  async loadPlayerData(userId) {
    try {
      const [settingsResult, seedsResult, plantsResult, toolsResult] = await Promise.all([
        supabase
          .from('player_settings')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle(),
        supabase
          .from('player_seeds')
          .select('*')
          .eq('user_id', userId),
        supabase
          .from('player_plants')
          .select('*')
          .eq('user_id', userId),
        supabase
          .from('player_tools')
          .select('*')
          .eq('user_id', userId),
      ])

      if (settingsResult.error) throw settingsResult.error
      if (seedsResult.error) throw seedsResult.error
      if (plantsResult.error) throw plantsResult.error
      if (toolsResult.error) throw toolsResult.error

      const seedInventory = (seedsResult.data || []).reduce((acc, seed) => {
        acc[seed.seed_type] = seed.quantity
        return acc
      }, {})

      const plants = (plantsResult.data || []).map(mapPlantRowToState)

      const ownedTools = (toolsResult.data || [])
        .filter((row) => row.category === 'tool')
        .map((row) => row.tool_id)

      const ownedBackgrounds = (toolsResult.data || [])
        .filter((row) => row.category === 'background')
        .map((row) => row.tool_id)

      const hasData = Boolean(
        settingsResult.data ||
        (seedsResult.data && seedsResult.data.length) ||
        (plantsResult.data && plantsResult.data.length) ||
        (toolsResult.data && toolsResult.data.length)
      )

      if (!hasData) {
        return { data: null, error: null }
      }

      const combinedData = {
        ...(settingsResult.data || {}),
        seed_inventory: seedInventory,
        owned_tools: ownedTools,
        owned_backgrounds: ownedBackgrounds,
        plants,
        herbarium: settingsResult.data?.herbarium || {},
        seeds: seedInventory.default ?? 0,
      }

      return { data: combinedData, error: null }
    } catch (error) {
      console.error('Error in loadPlayerData:', error)
      return { data: null, error }
    }
  },
  
  // Publish plant to community
  async publishPlant(userId, plantData) {
    const { data, error } = await supabase
      .from('community_plants')
      .insert({
        user_id: userId,
        plant_data: plantData,
        likes: 0,
      })
      .select()
      .single()
    
    if (error) throw error
    return data
  },
  
  // Get community plants
  async getCommunityPlants(limit = 50, offset = 0) {
    const { data, error } = await supabase
      .from('community_plants')
      .select('*')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)
    
    if (error) throw error
    return data
  },
  
  // Like a plant
  async likePlant(plantId, userId) {
    // Check if already liked
    const { data: existingLike } = await supabase
      .from('plant_likes')
      .select('*')
      .eq('plant_id', plantId)
      .eq('user_id', userId)
      .maybeSingle()
    
    if (existingLike) {
      // Unlike
      await supabase
        .from('plant_likes')
        .delete()
        .eq('plant_id', plantId)
        .eq('user_id', userId)
      
      await supabase.rpc('decrement_plant_likes', { plant_id: plantId })
    } else {
      // Like
      await supabase
        .from('plant_likes')
        .insert({ plant_id: plantId, user_id: userId })
      
      await supabase.rpc('increment_plant_likes', { plant_id: plantId })
    }
  },
  
  // Get player profile with robust error handling
  async getProfile(userId) {
    try {
      if (!userId) {
        console.error('getProfile: Missing userId')
        return { data: null, error: new Error('User ID is required') }
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()
      
      if (error) {
        console.error('Database error in getProfile:', error)
        return { data: null, error }
      }
      
      return { data: data || null, error: null }
    } catch (error) {
      console.error('Unexpected error in getProfile:', error)
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error(String(error))
      }
    }
  },
  
  // Update player profile
  async updateProfile(userId, updates) {
    const { error } = await supabase
      .from('profiles')
      .upsert({
        user_id: userId,
        ...updates,
        updated_at: new Date().toISOString(),
      })
    
    if (error) throw error
  },
  
  // Save subscription status
  async saveSubscription(userId, subscriptionData) {
    const { error } = await supabase
      .from('subscriptions')
      .upsert({
        user_id: userId,
        stripe_subscription_id: subscriptionData.id,
        stripe_customer_id: subscriptionData.customer,
        status: subscriptionData.status,
        plan_id: subscriptionData.items.data[0].price.id,
        current_period_end: new Date(subscriptionData.current_period_end * 1000).toISOString(),
        updated_at: new Date().toISOString(),
      })
    
    if (error) throw error
  },
  
    // Get subscription status with robust error handling
  async getSubscription(userId) {
    try {
      if (!userId) {
        console.error('getSubscription: Missing userId')
        return { data: null, error: new Error('User ID is required') }
      }

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()
      
      if (error) {
        console.error('Database error in getSubscription:', error)
        return { data: null, error }
      }
      
      return { data: data || null, error: null }
    } catch (error) {
      console.error('Unexpected error in getSubscription:', error)
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error(String(error))
      }
    }
  },
  

}

// Export individual functions for components that import them directly
export const getCommunityPlants = db.getCommunityPlants.bind(db)
export const publishPlant = db.publishPlant.bind(db)
export const likePlant = db.likePlant.bind(db)
export const getProfile = db.getProfile.bind(db)
export const updateProfile = db.updateProfile.bind(db)

// Authentication functions
export const auth = {
  // Get current session
  getSession: async () => {
    return await supabase.auth.getSession()
  },

  // Get current user
  getUser: async () => {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.user || null
  },

  // Sign in with email and password
  signIn: async (email, password) => {
    return await supabase.auth.signInWithPassword({ email, password })
  },

  // Sign up new user
  signUp: async (email, password, userMetadata = {}) => {
    return await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userMetadata
      }
    })
  },

  // Sign out
  signOut: async () => {
    return await supabase.auth.signOut()
  },

  // Reset password
  resetPassword: async (email) => {
    return await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`
    })
  },

  // Update user profile
  updateProfile: async (updates) => {
    return await supabase.auth.updateUser({
      data: updates
    })
  },

  // Listen to auth state changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback)
  }
}
